import React, { useEffect, useMemo, useState } from "react";
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, Legend, LinearScale, Tooltip } from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { Link } from "react-router-dom";
import MainLayout from "../components/layout/MainLayout";
import PageLoader from "../components/common/PageLoader";
import api from "../services/api";
import "./Dashboard.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const fmt = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const statusLabel = (value) => {
  const map = {
    1: "Present",
    2: "Absent",
    3: "Leave",
    4: "Half Day",
    5: "Paid Leave",
  };

  return map[value] || "Marked";
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard")
      .then((response) => setData(response.data?.data || null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <MainLayout><PageLoader force copy="Loading dashboard..." /></MainLayout>;
  }

  return (
    <MainLayout>
      <div className="mk-dashboard">
        <div className="mk-page-header">
          <div>
            <span className="mk-page-kicker">Workspace Overview</span>
            <h3>Dashboard</h3>
            <p>
              Welcome back, <strong>{data?.name || "User"}</strong>
            </p>
          </div>
          <span className="mk-role-badge">{data?.role || "Employee"}</span>
        </div>

        {data?.role === "Company Admin" ? <CompanyAdminDash data={data} /> : <EmployeeDash data={data} />}
      </div>
    </MainLayout>
  );
}

function CompanyAdminTodo({ data }) {
  const tasks = [];

  if (Number(data?.totalEmployees || 0) <= 1) {
    tasks.push({
      key: "employees",
      title: "Add your team",
      description: "Create employee records so attendance, salary setup, and payroll can start smoothly.",
      path: "/people/list",
      cta: "Open People",
    });
  }

  if (Number(data?.activeEmployees || 0) > 0) {
    tasks.push({
      key: "salary",
      title: "Review salary structures",
      description: "Confirm the latest salary structure, allowances, and statutory setup for your team.",
      path: "/salary",
      cta: "Open Salary",
    });
  }

  if (!data?.paymentDate) {
    tasks.push({
      key: "payroll",
      title: "Generate your first payroll",
      description: "Once attendance and salary structures are ready, process the payroll for the current cycle.",
      path: "/payroll/generate",
      cta: "Generate Payroll",
    });
  }

  if (!tasks.length) {
    return null;
  }

  return (
    <div className="mk-card mk-todo-card">
      <div className="mk-card-header mk-todo-header">
        <div>
          <h5 className="mk-card-title">To Do</h5>
          <p className="mk-todo-subtitle">
            Suggested next steps for completing your workspace setup.
          </p>
        </div>
        <span className="mk-todo-badge">{tasks.length} open</span>
      </div>
      <div className="mk-todo-list">
        {tasks.map((task, index) => (
          <div className="mk-todo-item" key={task.key}>
            <div className="mk-todo-index">{index + 1}</div>
            <div className="mk-todo-copy">
              <strong>{task.title}</strong>
              <p>{task.description}</p>
            </div>
            <Link to={task.path} className="mk-todo-link">
              {task.cta}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompanyAdminDash({ data }) {
  const deductionData = useMemo(
    () => ({
      labels: ["Net Pay", "Tax Deducted at Source", "Provident Fund", "Employee State Insurance", "Professional Tax"],
      datasets: [
        {
          data: [
            data.netPayrollAmt || 0,
            data.tenetivetds || 0,
            data.totalEpf || 0,
            data.totalEsi || 0,
            data.professionalTax || 0,
          ],
          backgroundColor: ["#6557ff", "#14b8a6", "#f59e0b", "#3b82f6", "#ef4444"],
          borderWidth: 0,
        },
      ],
    }),
    [data],
  );

  const complianceCards = [
    { label: "Processing Date", value: data.paymentDate || "Pending", tone: "neutral" },
    { label: "Tax Deducted at Source", value: fmt(data.tenetivetds), tone: "rose" },
    { label: "Provident Fund", value: fmt(data.totalEpf), tone: "amber" },
    { label: "Employee State Insurance", value: fmt(data.totalEsi), tone: "sky" },
  ];

  return (
    <>
      <div className="mk-payroll-banner">
        <div className="mk-payroll-banner-left">
          <span className="mk-banner-kicker">Monthly payroll snapshot</span>
          <p className="mk-payroll-month">
            Payroll Summary <strong>{data.payrollMonth}</strong>
          </p>
          <h2 className="mk-payroll-amount">{fmt(data.netPayrollAmt)}</h2>
          <p className="mk-payroll-sub">
            Employees net pay
            {data.paymentDate ? <span className="mk-payment-date"> | Processing date: {data.paymentDate}</span> : null}
          </p>
        </div>
        <div className="mk-payroll-banner-right">
          <Link to="/payroll/list" className="mk-action-btn ghost">
            View Payroll
          </Link>
          <Link to="/salary" className="mk-action-btn primary">
            Review Salary Structure
          </Link>
        </div>
      </div>

      <div className="mk-insight-strip">
        {complianceCards.map((card) => (
          <div key={card.label} className={`mk-insight-card tone-${card.tone}`}>
            <span>{card.label}</span>
            <strong>{card.value}</strong>
          </div>
        ))}
      </div>

      <CompanyAdminTodo data={data} />

      <div className="mk-stat-cards">
        <StatCard icon="fa-users" iconBg="#e0edff" iconColor="#2563eb" label="Employees" value={`${data.activeEmployees || 0} active`} sub={`${data.totalEmployees || 0} total`} subColor="#10b981" />
        <StatCard icon="fa-user-check" iconBg="#dcfce7" iconColor="#16a34a" label="Clocked In Today" value={data.todayPresents || 0} sub="Present and half day" />
        <StatCard icon="fa-receipt" iconBg="#fce7f3" iconColor="#db2777" label="Tax Deducted at Source" value={fmt(data.tenetivetds)} sub={data.payrollMonth} />
        <StatCard icon="fa-building-columns" iconBg="#fef3c7" iconColor="#ca8a04" label="Provident Fund" value={fmt(data.totalEpf)} sub={data.payrollMonth} />
      </div>

      <div className="mk-grid mt-4">
        <div className="mk-card">
          <div className="mk-card-header">
            <h5 className="mk-card-title">Deduction Breakdown</h5>
          </div>
          <div className="mk-doughnut-wrap">
            <Doughnut
              data={deductionData}
              options={{
                responsive: true,
                cutout: "68%",
                plugins: {
                  legend: {
                    position: "bottom",
                    labels: { font: { size: 12 }, padding: 16 },
                  },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => ` ${ctx.label}: ${fmt(ctx.parsed)}`,
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="mk-card">
          <div className="mk-card-header">
            <h5 className="mk-card-title">Recent Attendance</h5>
            <Link to="/attendance/view" className="mk-view-all-link">
              View all
            </Link>
          </div>
          <AttendanceChart entries={data.recentAttendance || []} />
        </div>
      </div>
    </>
  );
}

function EmployeeDash({ data }) {
  const personalHighlights = [
    { label: "Processing Date", value: data.paymentDate || "Pending", tone: "neutral" },
    { label: "Professional Tax", value: fmt(data.professionalTax), tone: "slate" },
    { label: "Provident Fund", value: fmt(data.totalEpf), tone: "amber" },
    { label: "Employee State Insurance", value: fmt(data.totalEsi), tone: "sky" },
  ];

  return (
    <>
      <div className="mk-payroll-banner">
        <div className="mk-payroll-banner-left">
          <span className="mk-banner-kicker">Monthly payroll snapshot</span>
          <p className="mk-payroll-month">
            Payroll Summary <strong>{data.payrollMonth}</strong>
          </p>
          <h2 className="mk-payroll-amount">{fmt(data.netPayrollAmt)}</h2>
          <p className="mk-payroll-sub">
            Your net pay
            {data.paymentDate ? <span className="mk-payment-date"> | Processing date: {data.paymentDate}</span> : null}
          </p>
        </div>
        <div className="mk-payroll-banner-right">
          <Link to="/payroll/list" className="mk-action-btn ghost">
            View Payslips
          </Link>
        </div>
      </div>

      <div className="mk-insight-strip">
        {personalHighlights.map((card) => (
          <div key={card.label} className={`mk-insight-card tone-${card.tone}`}>
            <span>{card.label}</span>
            <strong>{card.value}</strong>
          </div>
        ))}
      </div>

      <div className="mk-stat-cards">
        <StatCard icon="fa-wallet" iconBg="#e0edff" iconColor="#2563eb" label="Net Pay" value={fmt(data.netPayrollAmt)} sub={data.payrollMonth} />
        <StatCard icon="fa-receipt" iconBg="#fce7f3" iconColor="#db2777" label="Tax Deducted at Source" value={fmt(data.tenetivetds)} sub={data.payrollMonth} />
        <StatCard icon="fa-building-columns" iconBg="#fef3c7" iconColor="#ca8a04" label="Provident Fund" value={fmt(data.totalEpf)} sub={data.payrollMonth} />
        <StatCard icon="fa-shield-heart" iconBg="#dbeafe" iconColor="#2563eb" label="Employee State Insurance" value={fmt(data.totalEsi)} sub={data.payrollMonth} />
      </div>

      <div className="mk-grid mt-4">
        <div className="mk-card">
          <div className="mk-card-header">
            <h5 className="mk-card-title">Recent Attendance</h5>
            <Link to="/attendance/view" className="mk-view-all-link">
              View all
            </Link>
          </div>
          <AttendanceChart entries={data.recentAttendance || []} />
        </div>

        <div className="mk-card">
          <div className="mk-card-header">
            <h5 className="mk-card-title">Current Deductions</h5>
          </div>
          <div className="mk-deduction-rows">
            {[
              { label: "Tax Deducted at Source", value: data.tenetivetds },
              { label: "Provident Fund", value: data.totalEpf },
              { label: "Employee State Insurance", value: data.totalEsi },
              { label: "Professional Tax", value: data.professionalTax },
            ].map((row) => (
              <div className="mk-ded-row" key={row.label}>
                <span className="mk-ded-label">{row.label}</span>
                <span className="mk-ded-val">{fmt(row.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function StatCard({ icon, iconBg, iconColor, label, value, sub, subColor = "#9ca3af" }) {
  return (
    <div className="mk-stat-card" style={{ "--mk-stat-tint": iconBg }}>
      <div className="mk-stat-icon" style={{ color: iconColor }}>
        <i className={`fa-solid ${icon}`}></i>
      </div>
      <div>
        <p className="mk-stat-label">{label}</p>
        <h4 className="mk-stat-value">{value}</h4>
        {sub ? <p className="mk-stat-sub" style={{ color: subColor }}>{sub}</p> : null}
      </div>
    </div>
  );
}

function AttendanceChart({ entries }) {
  const chartData = useMemo(() => ({
    labels: entries.map((entry) => entry.date),
    datasets: [
      {
        label: "Attendance",
        data: entries.map((entry) => entry.status),
        backgroundColor: entries.map((entry) => {
          switch (entry.status) {
            case 1:
              return "#22c55e";
            case 2:
              return "#ef4444";
            case 3:
              return "#f59e0b";
            case 4:
              return "#3b82f6";
            case 5:
              return "#8b5cf6";
            default:
              return "#94a3b8";
          }
        }),
        borderRadius: 8,
        maxBarThickness: 38,
      },
    ],
  }), [entries]);

  return entries.length ? (
    <div style={{ height: 320 }}>
      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => statusLabel(ctx.raw),
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1,
                callback: (value) => statusLabel(value),
              },
            },
          },
        }}
      />
    </div>
  ) : (
    <p className="bs-empty-text">No attendance entries available.</p>
  );
}
